// WebSocket接続管理カスタムフック

import { useEffect, useRef, useState, useCallback } from "react";
import type {
  ClientMessage,
  ServerMessage,
  SerializedGameRoom,
} from "../types";

const BACKEND_URL = location.origin.replace("http", "ws");

type UseGameWebSocketProps = {
  guildId?: string;
  channelId?: string;
  userId?: string;
  username?: string;
  avatarUrl?: string;
  onStateUpdate: (state: SerializedGameRoom) => void;
};

export function useGameWebSocket({
  guildId,
  channelId,
  userId,
  username,
  avatarUrl,
  onStateUpdate,
}: UseGameWebSocketProps) {
  const wsRef = useRef<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);

  // メッセージ送信関数
  const sendMessage = useCallback((message: ClientMessage) => {
    console.log("Send message:", JSON.stringify(message));
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    } else {
      console.error("WebSocket is not connected");
    }
  }, []);

  // 接続関数
  const connect = useCallback(() => {
    // 必要な値が揃っていない場合は接続しない
    if (!guildId || !channelId) {
      console.log("Waiting for guildId and channelId...");
      return;
    }

    try {
      const wsUrl = `${BACKEND_URL}/game/${guildId}/${channelId}`;
      console.log("Connecting to WebSocket:", wsUrl);

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log("WebSocket connected");
        setIsConnected(true);
        setError(null);

        // 値がある場合は常にjoinメッセージを送信
        if (userId && username) {
          const joinMessage: ClientMessage = {
            type: "join",
            userId,
            username,
            avatarUrl,
          };
          ws.send(JSON.stringify(joinMessage));
        }
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as ServerMessage;

          if (message.type === "sync_state") {
            onStateUpdate(message.state);
          }
        } catch (error) {
          console.error("Failed to parse message:", error);
        }
      };

      ws.onerror = (event) => {
        console.error("WebSocket error:", event);
        setError("WebSocket connection error");
      };

      ws.onclose = () => {
        console.log("WebSocket disconnected");
        setIsConnected(false);

        // 自動再接続（5秒後）
        reconnectTimeoutRef.current = window.setTimeout(() => {
          console.log("Attempting to reconnect...");
          connect();
        }, 5000);
      };
    } catch (error) {
      console.error("Failed to create WebSocket:", error);
      setError("Failed to connect to game server");
    }
  }, [guildId, channelId, userId, username, avatarUrl, onStateUpdate]);

  // クリーンアップ関数
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setIsConnected(false);
  }, []);

  // 初回接続とクリーンアップ
  useEffect(() => {
    connect();

    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  return {
    isConnected,
    error,
    sendMessage,
    reconnect: connect,
  };
}
